// R115 calendar hub — B6 "click a date → choose what to schedule": the date
// chooser's Clean / Inspection / Task entries (inspector 'date' actions; the
// core lists them in the view's createOrder) and their form sheets. Each posts
// to the owning module's EXISTING route, tagged source:'calendar', answers
// every 409 inline, keeps typed values on an error, and then opens the new
// clean / inspection / task in the panel:
//   Clean       POST /api/assetops/turns {listing_id, date, type, note, by}
//               (409 needs_confirm → "Add anyway" = confirm_recent; 409 existing
//               → "Open that clean"), then optionally POST /turns/:id/assign
//               (409 capacity → "Assign anyway" = override)
//   Inspection  POST /api/assetops/inspections/assign {listing_id, checkout, inspector, manual}
//               (409 streak → "Assign anyway" = override; 409 moved_to → note)
//   Task        POST /api/tasks {title, …, due_date, listing_id, listing_name, link?}
// R115 integ fix (D2): opened from the toolbar "+ Add" there is no home yet (cell.listing_id ''): each form
// asks "Which home?" inline (../homePick) — Clean / Inspection need one before they post, a Task may stay an
// office task with no home. The chooser entries never gate on a home.
import { registerAction, type CalendarCtx, type DateCell, type EntityRef } from '../registry';
import {
  TURN_TYPES, TASK_PRIORITIES, chooserEnabled, cleanBody, inspBody, taskBody, guestInside, existingClean, existingInspection,
  type CapLayer, type TaskItem,
} from '../../../../../lib/calendarOps.mjs';
import { OFFICE, views, insp, propName, postJson, getJson, busy, fetchLayer, sheetHtml, trackDirty, say, createdTasks } from './util';
import { inspectorOptions } from './inspection';
import { homePickHtml, homePickEl, onHomePick } from '../homePick';

const cellOf = (ref: EntityRef, data: unknown): DateCell => {
  const d = (data || {}) as Partial<DateCell>;
  return { listing_id: String(d.listing_id || ref.listing_id || ''), date: String(d.date || ref.date || ''), past: !!d.past, taken: !!d.taken, stay_id: d.stay_id || null };
};
const turnsOf = (ctx: CalendarCtx) => (ctx.data.events && ctx.data.events.turns) || [];
const done = async (ctx: CalendarCtx, msg: string, reload: Parameters<CalendarCtx['reload']>[0], open: EntityRef) => {
  ctx.markDirty(false);
  ctx.closeSheet();
  ctx.toast(msg);
  await ctx.reload(reload);
  ctx.openInspector(open);
};
const headHtml = (ctx: CalendarCtx, lid: string, what: string, unset = 'Office') => `<b data-opv-headname>${ctx.esc(lid ? propName(ctx, lid) : unset)}</b><span>${ctx.esc(what)}</span>`;
/** The sheet head names the home the inline picker chose (D2). */
const nameHead = (ctx: CalendarCtx, root: HTMLElement, lid: string, unset: string) => { const b = root.querySelector('[data-opv-headname]'); if (b) b.textContent = lid ? propName(ctx, lid) : unset; };
const NEED_HOME = 'Pick the home first — which home is this for?';
const dateField = (ctx: CalendarCtx, date: string, min: string, max?: string) =>
  `<label class="opv-field"><span>Day</span><input type="date" data-opv-date value="${ctx.esc(date)}" min="${ctx.esc(min)}"${max ? ` max="${ctx.esc(max)}"` : ''} required></label>`;
const foot = (label: string) => `<button type="button" class="btn-secondary" data-opv-cancel>Cancel</button><button type="button" class="btn-primary" data-opv-save>${label}</button>`;
function shell(ctx: CalendarCtx, title: string, head: string, body: string, save: string): HTMLElement {
  const root = ctx.openSheet(sheetHtml(ctx, head, body, foot(save)), { title, onClose: () => ctx.markDirty(false) });
  trackDirty(ctx, root);
  root.querySelector('[data-opv-cancel]')?.addEventListener('click', () => { ctx.markDirty(false); ctx.closeSheet(); });
  return root;
}
const val = (root: HTMLElement, sel: string) => ((root.querySelector(sel) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value || '').trim();

// ── + Clean ─────────────────────────────────────────────────────────────────
export function openCleanForm(ctx: CalendarCtx, cell: DateCell): void {
  let lid = cell.listing_id;
  const body = (lid ? '' : homePickHtml(ctx, { field: 'opv-field' }))
    + dateField(ctx, cell.date, ctx.asOf)
    + `<div data-opv-exist></div>`
    + `<label class="opv-field"><span>Type</span><select data-opv-type>${TURN_TYPES.map(([k, l]) => `<option value="${k}">${ctx.esc(l)}</option>`).join('')}</select></label>`
    + `<label class="opv-field"><span>Cleaner <em>(optional)</em></span><select data-opv-cleaner><option value="">— assign later —</option></select><small class="opv-muted" data-opv-hint>Loading who’s working…</small></label>`
    + `<label class="opv-field"><span>Note <em>(optional)</em></span><textarea rows="2" maxlength="500" data-opv-note placeholder="Deep clean after the long stay, owner arriving…"></textarea></label>`;
  const root = shell(ctx, 'Add a clean', headHtml(ctx, lid, 'Deep clean, owner visit or make-ready — it lands on the Housekeeping board', 'Pick the home below'), body, 'Add clean');
  let created: { id: string } | null = null;   // a clean that already landed — a retry only re-assigns
  const refresh = async () => {
    const date = val(root, '[data-opv-date]');
    const ex = existingClean(turnsOf(ctx), lid, date);
    const exBox = root.querySelector('[data-opv-exist]') as HTMLElement;
    exBox.innerHTML = ex ? `<div class="opv-note opv-warn">Already has a ${ctx.esc(String(ex.status).replace('_', ' '))} clean that day. <button type="button" class="btn-ghost btn-sm" data-opv-open-turn="${ctx.esc(ex.id)}">Open it</button></div>` : '';
    exBox.querySelector('[data-opv-open-turn]')?.addEventListener('click', () => { ctx.markDirty(false); ctx.closeSheet(); ctx.openInspector({ kind: 'turn', id: ex!.id, listing_id: lid, date }); });
    const sel = root.querySelector('[data-opv-cleaner]') as HTMLSelectElement;
    const hint = root.querySelector('[data-opv-hint]') as HTMLElement;
    const keep = sel.value;
    try {
      const cap = await fetchLayer<CapLayer>('hk_capacity', date, date);
      const load = new Map(((cap.days[date] && cap.days[date].booked) || []).map(b => [b.id, b]));
      sel.innerHTML = `<option value="">— assign later —</option>` + cap.cleaners.map(c => {
        const b = load.get(c.id);
        // R115 review: "full" AT the cap (one more is what the assign route refuses), or a day off
        const full = !!b && (b.over || b.off || (b.cap != null && b.cleans >= b.cap));
        return `<option value="${ctx.esc(c.id)}"${c.id === keep ? ' selected' : ''}>${ctx.esc(c.name)}${c.kind === 'vendor' ? ' (vendor)' : ''}${b ? ` · ${b.cleans} that day${full ? (b.off ? ' · day off' : ' · full') : ''}` : ''}</option>`;
      }).join('');
      hint.textContent = 'Counts are that day’s cleans; “full” = at their daily cap (one more needs “Assign anyway”).';
    } catch { hint.textContent = 'Cleaner list unavailable — assign from the clean after saving.'; }
  };
  root.querySelector('[data-opv-date]')?.addEventListener('change', () => { void refresh(); });
  onHomePick(root, (v) => { if (created) return; lid = v; nameHead(ctx, root, lid, 'Pick the home below'); say(root, ''); void refresh(); });
  void refresh();
  const save = root.querySelector('[data-opv-save]') as HTMLButtonElement;
  const finish = (turnId: string, date: string, assigned: boolean) =>
    done(ctx, assigned ? 'Clean added and assigned' : 'Clean added — unassigned', { base: true }, { kind: 'turn', id: turnId, listing_id: lid, date });
  const assign = async (turnId: string, date: string, cleaner: string, override: boolean): Promise<void> => {
    say(root, 'Assigning…');
    const r = await busy(() => postJson(`/api/assetops/turns/${encodeURIComponent(turnId)}/assign`, { cleaner_ids: [cleaner], source: 'calendar', ...(override ? { override: true } : {}) }));
    if (r.ok) return finish(turnId, date, true);
    if (r.status === 409 && r.json && r.json.needs_confirm) {
      const m = say(root, `${ctx.esc(r.error)} <button type="button" class="btn-secondary btn-sm" data-opv-anyway>Assign anyway</button> <button type="button" class="btn-ghost btn-sm" data-opv-skip>Keep it unassigned</button>`, 'warn');
      m?.querySelector('[data-opv-anyway]')?.addEventListener('click', () => { void assign(turnId, date, cleaner, true); }, { once: true });
      m?.querySelector('[data-opv-skip]')?.addEventListener('click', () => { void finish(turnId, date, false); }, { once: true });
      return;
    }
    say(root, `The clean was added, but assigning failed: ${ctx.esc(r.error)} <button type="button" class="btn-ghost btn-sm" data-opv-skip>Keep it unassigned</button>`, 'bad');
    root.querySelector('[data-opv-skip]')?.addEventListener('click', () => { void finish(turnId, date, false); }, { once: true });
  };
  const create = async (confirmRecent: boolean): Promise<void> => {
    const date = val(root, '[data-opv-date]');
    if (!lid) { say(root, NEED_HOME, 'bad'); return; }
    const ok = chooserEnabled('clean', { date }, ctx.asOf);
    if (ok !== true) { say(root, ctx.esc(ok), 'bad'); return; }
    const cleaner = val(root, '[data-opv-cleaner]');
    if (created) { if (cleaner) return assign(created.id, date, cleaner, false); return finish(created.id, date, false); }
    save.disabled = true; say(root, 'Adding…');
    const r = await busy(() => postJson('/api/assetops/turns', cleanBody({ listing_id: lid, date, type: val(root, '[data-opv-type]'), note: val(root, '[data-opv-note]'), confirm_recent: confirmRecent }, ctx.actorName())));
    save.disabled = false;
    if (r.ok && r.json && r.json.turn) {
      created = { id: String(r.json.turn.id) };
      const hp = homePickEl(root); if (hp) hp.disabled = true;   // the clean landed at that home — a retry only re-assigns
      if (cleaner) return assign(created.id, date, cleaner, false);
      return finish(created.id, date, false);
    }
    if (r.status === 409 && r.json && r.json.needs_confirm) {
      const m = say(root, `${ctx.esc(r.error)} <button type="button" class="btn-secondary btn-sm" data-opv-anyway>Add anyway</button>`, 'warn');
      m?.querySelector('[data-opv-anyway]')?.addEventListener('click', () => { void create(true); }, { once: true });
      return;
    }
    if (r.status === 409 && r.json && r.json.existing) {
      const ex = r.json.existing;
      const m = say(root, `A clean already exists that day (${ctx.esc(ex.status)}). <button type="button" class="btn-secondary btn-sm" data-opv-open-ex>Open that clean</button>`, 'warn');
      m?.querySelector('[data-opv-open-ex]')?.addEventListener('click', () => { ctx.markDirty(false); ctx.closeSheet(); ctx.openInspector({ kind: 'turn', id: String(ex.id), listing_id: lid, date }); }, { once: true });
      return;
    }
    say(root, ctx.esc(r.error), 'bad');
  };
  save.addEventListener('click', () => { void create(false); });
}

// ── + Inspection ────────────────────────────────────────────────────────────
export function openInspectionForm(ctx: CalendarCtx, cell: DateCell): void {
  let lid = cell.listing_id;
  let L = insp(ctx);
  const max = ((): string => { const t = new Date(ctx.asOf + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + 60); return t.toISOString().slice(0, 10); })();
  const body = (lid ? '' : homePickHtml(ctx, { field: 'opv-field' }))
    + dateField(ctx, cell.date, ctx.asOf, max)
    + `<label class="opv-field"><span>Inspector</span><select data-opv-sel required>${inspectorOptions(ctx, L, '', L ? '— pick someone —' : 'Loading inspectors…')}</select></label>`
    + `<div data-opv-warn></div>`
    + `<div data-opv-lnote></div>`;
  const root = shell(ctx, 'Schedule an inspection', headHtml(ctx, lid, 'It lands in the Inspections queue on that day', 'Pick the home below'), body, 'Schedule');
  const warn = () => {
    const date = val(root, '[data-opv-date]');
    if (!lid) { (root.querySelector('[data-opv-warn]') as HTMLElement).innerHTML = ''; return; }
    const ex = existingInspection(L ? L.jobs : [], lid, date);
    const notes: string[] = [];
    if (ex) notes.push(`<div class="opv-note opv-info">This day already has ${ex.kind === 'checkout' ? 'the checkout' : 'an'} inspection (${ctx.esc(ex.inspector || 'unassigned')}) — saving assigns ${ex.inspector ? 'it to someone else' : 'it'}.</div>`);
    if (guestInside(ctx.data.stays, lid, date)) notes.push(`<div class="opv-note opv-warn">A guest is staying that night — the inspection would need them out.</div>`);
    (root.querySelector('[data-opv-warn]') as HTMLElement).innerHTML = notes.join('');
  };
  root.querySelector('[data-opv-date]')?.addEventListener('change', warn);
  onHomePick(root, (v) => { lid = v; nameHead(ctx, root, lid, 'Pick the home below'); say(root, ''); warn(); });
  warn();
  // R115 review: outside Ops / Cleans & Inspections the view doesn't load the inspections layer — fetch it for the form
  if (!L) void ctx.ensureLayer('inspections').then(() => {
    if (!root.isConnected) return;
    L = insp(ctx);
    const sel = root.querySelector('[data-opv-sel]') as HTMLSelectElement | null;
    if (sel) { const keep = sel.value; sel.innerHTML = inspectorOptions(ctx, L, keep, '— pick someone —'); }
    const note = root.querySelector('[data-opv-lnote]') as HTMLElement | null;
    if (note) note.innerHTML = L ? '' : `<div class="opv-note opv-warn">The inspection list couldn't be read, so existing jobs on this day can't be shown.</div>`;
    warn();
  });
  const save = root.querySelector('[data-opv-save]') as HTMLButtonElement;
  const send = async (override: boolean): Promise<void> => {
    const date = val(root, '[data-opv-date]');
    const inspector = val(root, '[data-opv-sel]');
    if (!lid) { say(root, NEED_HOME, 'bad'); return; }
    const ok = chooserEnabled('inspection', { date }, ctx.asOf);
    if (ok !== true) { say(root, ctx.esc(ok), 'bad'); return; }
    if (!inspector) { say(root, 'Pick an inspector — a scheduled inspection needs someone.', 'bad'); return; }
    const ex = existingInspection(L ? L.jobs : [], lid, date);
    save.disabled = true; say(root, 'Scheduling…');
    // a live checkout that day backs the job even when the inspections layer didn't carry it (the server checks too)
    const backed = ctx.data.stays.some(s => s.listing_id === lid && s.kind !== 'inquiry' && s.check_out === date);
    const r = await busy(() => postJson('/api/assetops/inspections/assign', inspBody({ listing_id: lid, date, inspector, override, backed }, ex)));
    save.disabled = false;
    if (r.ok) return done(ctx, `Inspection scheduled · ${inspector}`, { layers: ['inspections'] }, { kind: 'inspection', id: `${lid}|${date}`, listing_id: lid, date });
    if (r.status === 409 && r.json && r.json.needs_confirm) {
      const m = say(root, `${ctx.esc(r.error)} <button type="button" class="btn-secondary btn-sm" data-opv-anyway>Assign anyway</button>`, 'warn');
      m?.querySelector('[data-opv-anyway]')?.addEventListener('click', () => { void send(true); }, { once: true });
      return;
    }
    if (r.status === 409 && r.json && r.json.moved_to) { say(root, `This day's inspection was moved to ${ctx.esc(ctx.fmt.md(r.json.moved_to))} — open it there or pick another day.`, 'warn'); return; }
    say(root, ctx.esc(r.error), 'bad');
  };
  save.addEventListener('click', () => { void send(false); });
}

// ── + Task ─────────────────────────────────────────────────────────────────
interface Person { email: string; full_name: string; role: string }
let peopleP: Promise<Person[]> | null = null;
const people = (): Promise<Person[]> => (peopleP ||= getJson<{ people: Person[] }>('/api/tasks/assignees').then(r => {
  if (!r.ok || !r.json) { peopleP = null; return []; }
  return (r.json.people || []).filter(p => p && p.email);
}));
export function openTaskForm(ctx: CalendarCtx, cell: DateCell): void {
  let lid = cell.listing_id;
  const stay = cell.stay_id ? ctx.data.stays.find(s => s.id === cell.stay_id) : null;
  const body = `<label class="opv-field"><span>What needs doing</span><input type="text" maxlength="200" data-opv-title placeholder="Replace the porch light" required></label>`
    + (lid ? '' : homePickHtml(ctx, { field: 'opv-field', none: 'No home — office task' }))
    + dateField(ctx, cell.date, ctx.asOf)
    + `<label class="opv-field"><span>Priority</span><select data-opv-prio>${TASK_PRIORITIES.map(([k, l]) => `<option value="${k}">${ctx.esc(l)}</option>`).join('')}</select></label>`
    + `<fieldset class="opv-field opv-people"><legend>Assign to <em>(optional)</em></legend><div data-opv-people class="opv-people-list"><span class="opv-muted">Loading people…</span></div></fieldset>`
    + `<label class="opv-field"><span>Details <em>(optional)</em></span><textarea rows="3" maxlength="4000" data-opv-desc></textarea></label>`
    + (stay ? `<label class="opv-check"><input type="checkbox" data-opv-link checked> Link it to the ${ctx.esc(ctx.fmt.md(stay.check_in))}–${ctx.esc(ctx.fmt.md(stay.check_out))} reservation</label>` : '');
  const root = shell(ctx, 'Add a task', headHtml(ctx, lid, 'It lands on the Tasks board, due that day'), body, 'Add task');
  onHomePick(root, (v) => { lid = v; nameHead(ctx, root, lid, 'Office'); });
  void people().then(list => {
    const box = root.querySelector('[data-opv-people]') as HTMLElement | null;
    if (!box) return;
    box.innerHTML = list.length
      ? list.map(p => `<label class="opv-check"><input type="checkbox" value="${ctx.esc(p.email)}" data-opv-person> ${ctx.esc(p.full_name || 'Unnamed')}${p.role ? ` <small class="opv-muted">${ctx.esc(p.role)}</small>` : ''}</label>`).join('')
      : `<span class="opv-muted">The people list is unavailable — assign on the Tasks page.</span>`;
  });
  const save = root.querySelector('[data-opv-save]') as HTMLButtonElement;
  save.addEventListener('click', async () => {
    const title = val(root, '[data-opv-title]');
    const date = val(root, '[data-opv-date]');
    if (!title) { say(root, 'Give the task a title.', 'bad'); return; }
    const ok = chooserEnabled('task', { date }, ctx.asOf);
    if (ok !== true) { say(root, ctx.esc(ok), 'bad'); return; }
    const assignees = [...root.querySelectorAll<HTMLInputElement>('[data-opv-person]:checked')].map(x => x.value);
    const link = !!(root.querySelector('[data-opv-link]') as HTMLInputElement | null)?.checked;
    const p = ctx.property(lid);
    const b = taskBody({ listing_id: lid, listing_name: p ? p.name : '', date, title, description: val(root, '[data-opv-desc]'), priority: val(root, '[data-opv-prio]'), assignees, stay_id: link && stay ? stay.id : null });
    save.disabled = true; say(root, 'Adding…');
    const r = await busy(() => postJson('/api/tasks', b));
    save.disabled = false;
    if (!r.ok || !r.json || !r.json.task) { say(root, ctx.esc(r.error || 'Could not add the task'), 'bad'); return; }
    const t = r.json.task;
    const names = await people().catch(() => [] as Person[]);
    const nm = (e: string) => { const x = names.find(q => q.email.toLowerCase() === String(e).toLowerCase()); return x ? x.full_name : 'Assigned'; };
    const item: TaskItem = { id: String(t.id), title: String(t.title || title), status: String(t.status || 'todo'), priority: String(t.priority || 'normal'), due_date: date, listing_id: lid || null,
      assignees: (t.assignees || assignees).map((e: string) => { const n = nm(e); return { name: n, initials: n.split(/\s+/).map((w: string) => w[0] || '').join('').slice(0, 2).toUpperCase() || '?' }; }) };
    createdTasks.set(item.id, item);
    await done(ctx, 'Task added', { layers: ['tasks'] }, { kind: 'task', id: item.id, listing_id: lid || undefined, date });
  });
}

// ── the chooser entries ─────────────────────────────────────────────────────
// R115 integ fix (D2): no home yet ('' — the toolbar "+ Add") is fine: the form asks "Which home?" itself.
const enabled = (kind: string) => (ref: EntityRef, data: unknown, ctx: CalendarCtx) => chooserEnabled(kind, cellOf(ref, data), ctx.asOf) === true;
registerAction({
  key: 'clean', inspector: 'date', label: 'Clean', icon: '🧹', order: 20, roles: OFFICE, primaryIn: views('ops.hk'),
  when: enabled('clean'), run: (ref, data, ctx) => openCleanForm(ctx, cellOf(ref, data)),
});
registerAction({
  key: 'inspection', inspector: 'date', label: 'Inspection', icon: '🔍', order: 30, roles: OFFICE,
  when: enabled('inspection'), run: (ref, data, ctx) => openInspectionForm(ctx, cellOf(ref, data)),
});
registerAction({
  key: 'task', inspector: 'date', label: 'Task', icon: '✓', order: 40, roles: OFFICE, primaryIn: views('health'),
  when: enabled('task'), run: (ref, data, ctx) => openTaskForm(ctx, cellOf(ref, data)),
});
